import {
  BackupStatus,
  getBackupDirectoryHandle,
  getBackupEveryNSeconds,
  getBackupStatus,
  getLastFullBackup,
  getLatestBackup,
  setBackupDirectoryHandle,
  setBackupPath,
  setBackupStatus,
  setLastFullBackup,
  setLatestBackup,
  setLatestBackupSize,
} from './prefs';
import Dexie from 'dexie';
import type { Table } from 'dexie';
import { importInto } from 'dexie-export-import';
import { db, getDBContext, DBPage, Tag } from './db';
import { now, debounce } from 'lodash';
import { reportProgress, clearProgress } from './progress';
import { logger } from '../utils/logger';

// chrome.system.memory is available in extension service workers; performance.memory
// is only available in Window contexts (not service workers), so we prefer the former.
async function memUsageMB(): Promise<string> {
  try {
    if (typeof chrome !== 'undefined' && chrome.system?.memory) {
      const info = await chrome.system.memory.getInfo();
      const freeMB = Math.round(info.availableCapacity / 1024 / 1024);
      const totalMB = Math.round(info.capacity / 1024 / 1024);
      return `${freeMB}/${totalMB}MB free`;
    }
  } catch (_) {}
  return 'n/a';
}

const BACKUP_BATCH_SIZE = 200;

// How often to write a full backup (all other backups are incremental).
const FULL_BACKUP_INTERVAL_MS = 24 * 60 * 60 * 1000; // 24 hours

// Retention policy constants (exported for tests).
export const MAX_INCREMENTALS = 5;
export const DAILY_RETENTION_DAYS = 14;
export const WEEKLY_RETENTION_WEEKS = 6;

// ---- Full export ----

/**
 * Export the DB in small batches, releasing the read transaction between batches.
 *
 * exportDB from dexie-export-import wraps the entire export in a single read-only
 * transaction that can span seconds for a large database. While that transaction
 * is open, any write transaction (e.g. addTag) must wait, blocking user interactions.
 *
 * This implementation uses one short-lived read transaction per batch, yielding to
 * the event loop between batches via setTimeout(0). Write transactions can slip in
 * during those yields, so the snapshot is not perfectly consistent — acceptable for
 * a backup file.
 */
async function chunkedExportDB(database: Dexie): Promise<ExportResult> {
  const tables = database.tables;

  const tableData: Array<{ name: string; schema: string; rowCount: number; rows: unknown[] }> = [];

  for (const table of tables as Table[]) {
    const { primKey, indexes } = table.schema;
    const schemaStr = [primKey.src, ...indexes.map((i) => i.src)].filter(Boolean).join(',');

    const rows: unknown[] = [];
    let offset = 0;

    while (true) {
      // Each call opens its own short read transaction and closes it before returning.
      const batch = await table.offset(offset).limit(BACKUP_BATCH_SIZE).toArray();
      rows.push(...batch);
      if (batch.length < BACKUP_BATCH_SIZE) break;
      offset += BACKUP_BATCH_SIZE;
      // Yield to the macrotask queue so pending write transactions can proceed.
      await new Promise<void>((resolve) => setTimeout(resolve, 0));
    }

    tableData.push({ name: table.name, schema: schemaStr, rowCount: rows.length, rows });
    logger.debug(`[Backup] Exported table '${table.name}': ${rows.length} rows`);
  }

  const totalRows = tableData.reduce((s, t) => s + t.rowCount, 0);
  logger.debug(`[Backup] DB export: ${tableData.length} tables, ${totalRows} total rows`);

  const exportData = {
    formatName: 'dexie',
    formatVersion: 1,
    data: {
      databaseName: database.name,
      databaseVersion: database.verno,
      tables: tableData,
    },
  };

  const blob = new Blob([JSON.stringify(exportData)], { type: 'application/json' });
  const pageCount = tableData.find((t) => t.name === 'pages')?.rowCount ?? 0;
  const tagCount = tableData.find((t) => t.name === 'tags')?.rowCount ?? 0;
  return { blob, pageCount, tagCount };
}

interface ExportResult {
  blob: Blob;
  pageCount: number;
  tagCount: number;
}

// ---- Incremental export ----

/**
 * Export only pages accessed/created since sinceTimestamp, plus all tags.
 *
 * Uses the lastActiveDesc index (lastActiveDesc = -lastAccessTime) to efficiently
 * find recently-active pages without a full table scan. Tag-only changes to pages
 * not recently visited are missed — the next daily full backup captures them.
 *
 * Reads pages in batches of BACKUP_BATCH_SIZE to avoid loading all embeddings into
 * memory at once (each page may carry a 384-dim float array, ~1.5 KB of JSON).
 */
async function chunkedExportIncremental(
  database: Dexie,
  sinceTimestamp: number,
  baseFullBackupName: string,
): Promise<ExportResult> {
  // lastActiveDesc is negated: more negative = more recent.
  // Pages accessed AFTER sinceTimestamp have lastActiveDesc < -sinceTimestamp.
  const cutoff = -sinceTimestamp;
  logger.debug(
    `[Backup] Incremental query: sinceTimestamp=${sinceTimestamp} (${new Date(sinceTimestamp).toISOString()}), cutoff=${cutoff}`,
  );

  const pagesTable = database.table('pages') as Table;

  // Chunked read: avoids accumulating all embeddings in one large in-memory array.
  const pages: unknown[] = [];
  let offset = 0;
  const t0 = Date.now();
  while (true) {
    const batch = await pagesTable
      .where('lastActiveDesc')
      .below(cutoff)
      .offset(offset)
      .limit(BACKUP_BATCH_SIZE)
      .toArray();
    pages.push(...batch);
    if (batch.length < BACKUP_BATCH_SIZE) break;
    offset += BACKUP_BATCH_SIZE;
    await new Promise<void>((resolve) => setTimeout(resolve, 0));
  }
  logger.debug(`[Backup] Incremental pages read: ${pages.length} pages in ${Date.now() - t0}ms`);

  // Yield before reading the next table.
  await new Promise<void>((resolve) => setTimeout(resolve, 0));

  // Always include all tags — they're small and capture tag renames/metadata changes.
  const t1 = Date.now();
  const tags = await database.table('tags').toArray();
  logger.debug(`[Backup] Incremental tags read: ${tags.length} tags in ${Date.now() - t1}ms`);

  const t2 = Date.now();
  const exportData = {
    type: 'tabvana-incremental',
    formatVersion: 1,
    basedOnTimestamp: sinceTimestamp,
    baseFullBackupName,
    data: { pages, tags },
  };
  const blob = new Blob([JSON.stringify(exportData)], { type: 'application/json' });
  logger.debug(
    `[Backup] Incremental serialized: ${(blob.size / 1024).toFixed(0)}KB in ${Date.now() - t2}ms`,
  );

  return { blob, pageCount: pages.length, tagCount: tags.length };
}

// ---- Thinning helpers (exported for tests) ----

export interface BackupFile {
  name: string;
  timestamp: number;
  type: 'full' | 'incremental';
}

/**
 * Parse a backup filename into a BackupFile descriptor.
 * Returns null if the name doesn't match any known backup pattern.
 *
 * Patterns:
 *   tabvana-full-{ts}.json   → full backup
 *   tabvana-incr-{ts}.json   → incremental backup
 *   tabvana-{ts}.json        → legacy format, treated as full
 */
export function parseBackupFilename(name: string): BackupFile | null {
  const fullMatch = name.match(/^tabvana-full-(\d+)\.json$/);
  if (fullMatch) return { name, timestamp: parseInt(fullMatch[1], 10), type: 'full' };

  const incrMatch = name.match(/^tabvana-incr-(\d+)\.json$/);
  if (incrMatch) return { name, timestamp: parseInt(incrMatch[1], 10), type: 'incremental' };

  const oldMatch = name.match(/^tabvana-(\d+)\.json$/);
  if (oldMatch) return { name, timestamp: parseInt(oldMatch[1], 10), type: 'full' };

  return null;
}

/** Return the ISO 8601 year-week string (e.g. "2026-W19") for a given timestamp. */
export function getISOWeekKey(timestamp: number): string {
  const d = new Date(timestamp);
  const utc = new Date(Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate()));
  const dayNum = utc.getUTCDay() || 7; // Mon=1 … Sun=7
  utc.setUTCDate(utc.getUTCDate() + 4 - dayNum); // move to Thursday of this week
  const yearStart = new Date(Date.UTC(utc.getUTCFullYear(), 0, 1));
  const weekNo = Math.ceil(((utc.getTime() - yearStart.getTime()) / 86400000 + 1) / 7);
  return `${utc.getUTCFullYear()}-W${String(weekNo).padStart(2, '0')}`;
}

/**
 * Given a list of backup files, return the names of files that should be deleted.
 *
 * Retention policy:
 *   - Incrementals: keep the most recent MAX_INCREMENTALS; delete the rest.
 *   - Full backups within DAILY_RETENTION_DAYS: keep all.
 *   - Full backups in the next WEEKLY_RETENTION_WEEKS weeks: keep one per ISO week
 *     (the earliest backup of each week, so we get the most spread).
 *   - Full backups older than daily + weekly window: delete all.
 */
export function computeFilesToDelete(files: BackupFile[], now: number = Date.now()): string[] {
  const dailyCutoff = now - DAILY_RETENTION_DAYS * 24 * 60 * 60 * 1000;
  const weeklyCutoff = dailyCutoff - WEEKLY_RETENTION_WEEKS * 7 * 24 * 60 * 60 * 1000;

  const incrementals = files
    .filter((f) => f.type === 'incremental')
    .sort((a, b) => b.timestamp - a.timestamp); // newest first

  const fulls = files
    .filter((f) => f.type === 'full')
    .sort((a, b) => b.timestamp - a.timestamp); // newest first

  const toKeep = new Set<string>();

  // Keep the most recent MAX_INCREMENTALS incrementals.
  for (const f of incrementals.slice(0, MAX_INCREMENTALS)) {
    toKeep.add(f.name);
  }

  // Keep all full backups within the daily window.
  for (const f of fulls) {
    if (f.timestamp >= dailyCutoff) toKeep.add(f.name);
  }

  // Weekly snapshots: one per ISO week beyond the daily window.
  // Sort oldest-first so the earliest backup of each week is chosen as representative.
  const weeklyWindow = fulls
    .filter((f) => f.timestamp < dailyCutoff && f.timestamp >= weeklyCutoff)
    .sort((a, b) => a.timestamp - b.timestamp);

  const weeksSeen = new Set<string>();
  for (const f of weeklyWindow) {
    const week = getISOWeekKey(f.timestamp);
    if (!weeksSeen.has(week)) {
      weeksSeen.add(week);
      toKeep.add(f.name);
    }
  }

  return files.filter((f) => !toKeep.has(f.name)).map((f) => f.name);
}

// ---- Backup entry points ----

const MODULE_LOADED_AT = Date.now();
const STARTUP_DELAY_MS = 30 * 1000; // 30 seconds

export async function hasBackupPermission(handle?: FileSystemDirectoryHandle): Promise<boolean> {
  if (!handle) {
    handle = await getBackupDirectoryHandle();
  }
  if (!handle) {
    return false;
  }
  return (await handle.queryPermission({ mode: 'readwrite' })) === 'granted';
}

export async function requestBackupPermission(): Promise<boolean> {
  const handle = await getBackupDirectoryHandle();
  if (!handle) {
    return false;
  }
  const result = await handle.requestPermission({ mode: 'readwrite' });
  if (result === 'granted') {
    await setBackupStatus(BackupStatus.DirectoryAccessible);
    return true;
  }
  return false;
}

export async function setBackupDir() {
  let dir: FileSystemDirectoryHandle;
  try {
    dir = await window.showDirectoryPicker({ mode: 'readwrite' });
  } catch (e) {
    if (e instanceof DOMException && e.name === 'AbortError') return;
    throw e;
  }
  logger.info('Set Backup Directory: ', dir);
  await setBackupDirectoryHandle(dir);
}

interface DoBackupResult {
  success: boolean;
  sizeMB?: number;
  pageCount?: number;
  tagCount?: number;
}

/**
 * Write one backup file (full or incremental) to the directory.
 * Returns success flag and size/count metadata on success, false on non-permission error.
 * Throws NotAllowedError so the caller can set PermissionRevoked status.
 */
async function doBackup(timestamp: number, isFull: boolean, lastFullTs: number): Promise<DoBackupResult> {
  const directory = await getBackupDirectoryHandle();
  try {
    if (directory == null) {
      throw new Error('No backup directory selected');
    }
    const prefix = isFull ? 'full' : 'incr';
    const file = await directory.getFileHandle(`tabvana-${prefix}-${timestamp}.json`, { create: true });

    logger.debug(`[Backup] Starting ${prefix} DB export. Mem: ${await memUsageMB()}`);
    const exportStart = Date.now();
    const baseFullBackupName = `tabvana-full-${lastFullTs}.json`;
    const { blob, pageCount, tagCount } = isFull
      ? await chunkedExportDB(db)
      : await chunkedExportIncremental(db, lastFullTs, baseFullBackupName);
    const exportMs = Date.now() - exportStart;
    logger.debug(
      `[Backup] ${prefix} export done: ${(blob.size / 1024 / 1024).toFixed(1)}MB in ${exportMs}ms. Mem: ${await memUsageMB()}`,
    );

    const writable = await file.createWritable();
    const writeStart = Date.now();
    await writable.write(blob);
    logger.debug(`[Backup] write() done in ${Date.now() - writeStart}ms`);
    const closeStart = Date.now();
    await writable.close();
    logger.debug(
      `[Backup] close() done in ${Date.now() - closeStart}ms. Mem: ${await memUsageMB()}`,
    );

    await setLatestBackup(timestamp);
    await setLatestBackupSize(blob.size);
    return { success: true, sizeMB: blob.size / 1024 / 1024, pageCount, tagCount };
  } catch (e) {
    if (e instanceof DOMException && e.name === 'NotAllowedError') {
      throw e; // propagate so backupAndThin can set PermissionRevoked status
    }
    logger.error('Backup failed:', e);
    return { success: false };
  }
}

let isBackingUp = false;

async function backupAndThin() {
  if (isBackingUp) {
    return;
  }
  isBackingUp = true;
  const backupStart = Date.now();
  logger.debug(`[Backup] Backup starting. Mem: ${await memUsageMB()}`);
  const taskId = 'backup-task';
  let isCompleted = false;
  let backupResult: DoBackupResult = { success: false };
  let thinned = 0;
  let totalFiles = 0;
  try {
    await reportProgress(taskId, 'background', 'backup', 0, 3);
    await setBackupStatus(BackupStatus.BackupInProgress);

    if (!(await hasBackupPermission())) {
      await setBackupStatus(BackupStatus.DirectoryNotAccessible);
      return;
    }
    await reportProgress(taskId, 'background', 'backup', 1, 3);

    const lastFullTs = await getLastFullBackup();
    const isFull = !lastFullTs || Date.now() - lastFullTs >= FULL_BACKUP_INTERVAL_MS;
    const timestamp = Date.now();

    backupResult = await doBackup(timestamp, isFull, lastFullTs);
    if (backupResult.success && isFull) {
      await setLastFullBackup(timestamp);
    }

    await reportProgress(taskId, 'background', 'backup', 2, 3);

    const directory = await getBackupDirectoryHandle();
    if (directory == null) {
      throw new Error('No backup directory selected');
    }

    // Collect all recognised backup files in the directory.
    const thinStart = Date.now();
    const backupFiles: BackupFile[] = [];
    for await (const entry of directory.values()) {
      if (entry.kind === 'file') {
        const file = await entry.getFile();
        const parsed = parseBackupFilename(file.name);
        if (parsed) backupFiles.push(parsed);
      }
    }

    // Apply retention policy.
    const toDelete = computeFilesToDelete(backupFiles);
    for (const name of toDelete) {
      await directory.removeEntry(name);
    }
    thinned = toDelete.length;
    totalFiles = backupFiles.length;
    logger.debug(
      `[Backup] Thinning done: ${totalFiles} files, ${thinned} deleted, ${Date.now() - thinStart}ms`,
    );

    await setBackupStatus(BackupStatus.DirectoryAccessible);
    await reportProgress(taskId, 'background', 'backup', 3, 3);
    isCompleted = true;
  } catch (e) {
    if (e instanceof DOMException && e.name === 'NotAllowedError') {
      logger.error('[Backup] Permission revoked during backup:', e);
      await setBackupStatus(BackupStatus.PermissionRevoked);
    } else {
      logger.error('[Backup] backupAndThin failed:', e);
      await setBackupStatus(BackupStatus.DirectoryNotAccessible);
    }
  } finally {
    isBackingUp = false;
    const elapsedMs = Date.now() - backupStart;
    if (isCompleted && backupResult.success) {
      const { sizeMB = 0, pageCount = 0, tagCount = 0 } = backupResult;
      const thinnedStr = thinned > 0 ? ` | thinned ${thinned}/${totalFiles} files` : '';
      logger.info(
        `[Backup] Completed: ${sizeMB.toFixed(1)}MB | ${pageCount} pages | ${tagCount} tags${thinnedStr} | ${elapsedMs}ms`,
      );
    } else if (!isCompleted) {
      logger.debug(`[Backup] Backup failed/aborted in ${elapsedMs}ms`);
    }
    await clearProgress(taskId, isCompleted);
  }
}

export async function maybeBackup(force: boolean = false) {
  if (!force && Date.now() - MODULE_LOADED_AT < STARTUP_DELAY_MS) {
    return;
  }
  const lastBackup = await getLatestBackup();
  const backupEveryNSeconds = await getBackupEveryNSeconds();
  if (!force && backupEveryNSeconds == null) {
    return;
  }
  if (force || lastBackup == null || now() - lastBackup > backupEveryNSeconds * 1000) {
    await backupAndThin();
  }
}

export const debouncedMaybeBackup = debounce(() => maybeBackup(false), 5000);

interface IncrementalBackupData {
  type: 'tabvana-incremental';
  formatVersion: number;
  basedOnTimestamp: number;
  baseFullBackupName?: string;
  data: { pages: unknown[]; tags: unknown[] };
}

function isIncrementalBackup(parsed: unknown): parsed is IncrementalBackupData {
  return (
    typeof parsed === 'object' &&
    parsed !== null &&
    (parsed as Record<string, unknown>)['type'] === 'tabvana-incremental'
  );
}

async function overlayIncrementalData(pages: unknown[], tags: unknown[]): Promise<void> {
  const CHUNK = 500;
  for (let i = 0; i < pages.length; i += CHUNK) {
    await db.pages.bulkPut(pages.slice(i, i + CHUNK) as DBPage[]);
  }
  for (let i = 0; i < tags.length; i += CHUNK) {
    await db.tags.bulkPut(tags.slice(i, i + CHUNK) as Tag[]);
  }
}

export async function restore() {
  // Step 1: User picks the backup file (full or incremental).
  let files: FileSystemFileHandle[];
  try {
    files = await window.showOpenFilePicker();
  } catch (e) {
    if (e instanceof DOMException && e.name === 'AbortError') return;
    throw e;
  }

  const file = await files[0].getFile();
  const text = await file.text();

  let parsed: unknown;
  try {
    parsed = JSON.parse(text);
  } catch {
    throw new Error('Invalid backup file: not valid JSON');
  }

  if (!isIncrementalBackup(parsed)) {
    // Full backup in dexie-export-import format — import directly.
    await importInto(db, new Blob([text], { type: 'application/json' }), {
      clearTablesBeforeImport: true,
    });
    return;
  }

  // Incremental backup: locate the base full backup, restore it first, then overlay.
  const { basedOnTimestamp, baseFullBackupName, data } = parsed;
  const derivedFullName = baseFullBackupName ?? `tabvana-full-${basedOnTimestamp}.json`;

  // Try to auto-locate the base full backup in the configured backup directory.
  let fullBackupBlob: Blob | null = null;
  const dir = await getBackupDirectoryHandle();
  if (dir) {
    // Candidates in priority order: explicit name, derived name, legacy name.
    const candidates = [
      ...(baseFullBackupName ? [baseFullBackupName] : []),
      `tabvana-full-${basedOnTimestamp}.json`,
      `tabvana-${basedOnTimestamp}.json`,
    ];
    for (const candidate of [...new Set(candidates)]) {
      try {
        const handle = await dir.getFileHandle(candidate);
        fullBackupBlob = await handle.getFile();
        logger.info(`[Restore] Auto-found base full backup: ${candidate}`);
        break;
      } catch {
        // Not in the directory — try the next candidate.
      }
    }
  }

  if (!fullBackupBlob) {
    // Ask the user to manually select the base full backup.
    logger.info(`[Restore] Base full backup not found automatically; prompting user for ${derivedFullName}`);
    let fullFiles: FileSystemFileHandle[];
    try {
      fullFiles = await window.showOpenFilePicker(dir ? { startIn: dir } : undefined);
    } catch (e) {
      if (e instanceof DOMException && e.name === 'AbortError') {
        throw new Error(
          `Incremental restore requires the base full backup. Please also select: ${derivedFullName}`,
        );
      }
      throw e;
    }
    fullBackupBlob = await fullFiles[0].getFile();
  }

  // Step 1: Restore the full backup (clears the DB first).
  await importInto(db, fullBackupBlob, { clearTablesBeforeImport: true });

  // Step 2: Overlay incremental pages and tags on top of the restored full backup.
  await overlayIncrementalData(data.pages, data.tags);
}

if (getDBContext() === '[SW]') {
  Dexie.on('storagemutated', debouncedMaybeBackup);
}
